import React, { useState, useEffect } from 'react';
import createAxiosInstance from '../../utils/axiosConfig';
import { FrameworkCategory } from '../../utils/interfaces';
import { create_domain, create_question_flow, create_question, update_question, create_scoring_option, delete_domain, delete_question_flow, delete_question, delete_scoring_option } from '../../utils/endpoints';
import Alert from './alerts/alert';
import AdminTables from './adminTables';
import bgPublic from '../../assets/img/bg-public.png';
import grayBgTransparent from '../../assets/img/gray-bg-transparent.png';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface FrameworkSetupSidescreenProps {
  isOpen: boolean;
  onClose: () => void;
  frameworkData: FrameworkCategory | null;
}

interface DomainFormData {
  category_id: number;
  title: string;
  description: string;
  is_enabled: boolean;
  is_active: boolean;
}

interface QuestionFlowFormData {
  domain_id: number;
  title: string;
  description: string;
  overall_score: number;
  is_enabled: boolean;
  is_deleted: boolean;
}

interface QuestionFormData {
  flow_id: number;
  question_text: string;
  order: number;
  is_enabled: boolean;
  is_deleted: boolean;
}

interface ScoringOptionFormData {
  framework_id: number;
  option_name: string;
  score: number;
  tooltip_content: { [key: string]: string[] };
  is_enabled: boolean;
  is_deleted: boolean;
}

const FrameworkSetupSidescreen: React.FC<FrameworkSetupSidescreenProps> = ({
  isOpen,
  onClose,
  frameworkData
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<{ message: string; type: 'success' | 'danger' | 'warning'; show: boolean } | null>(null);
  
  // Domain-related state
  const [domains, setDomains] = useState<any[]>([]);
  const [domainFormData, setDomainFormData] = useState<DomainFormData>({
    category_id: 0, // Will be set in useEffect when frameworkData is available
    title: '',
    description: '',
    is_enabled: true,
    is_active: true
  });
  const [domainErrors, setDomainErrors] = useState<{ [key: string]: string }>({});

  // Question Flow related state
  const [questionFlows, setQuestionFlows] = useState<any[]>([]);
  const [questionFlowFormData, setQuestionFlowFormData] = useState<QuestionFlowFormData>({
    domain_id: 0,
    title: '',
    description: '',
    overall_score: 4,
    is_enabled: true,
    is_deleted: false
  });
  const [questionFlowErrors, setQuestionFlowErrors] = useState<{ [key: string]: string }>({});

  // Questions related state
  const [questions, setQuestions] = useState<any[]>([]);
  const [questionFormData, setQuestionFormData] = useState<QuestionFormData>({
    flow_id: 0,
    question_text: '',
    order: 1,
    is_enabled: true,
    is_deleted: false
  });
  const [questionErrors, setQuestionErrors] = useState<{ [key: string]: string }>({});

  // Scoring options related state
  const [scoringOptions, setScoringOptions] = useState<any[]>([]);
  const [scoringOptionFormData, setScoringOptionFormData] = useState<ScoringOptionFormData>({
    framework_id: 0, // Will be set in useEffect when frameworkData is available
    option_name: '',
    score: 0,
    tooltip_content: {},
    is_enabled: true,
    is_deleted: false
  });
  const [scoringOptionErrors, setScoringOptionErrors] = useState<{ [key: string]: string }>({});
  const [tooltipKeys, setTooltipKeys] = useState<string[]>(['']); // For dynamic tooltip fields

  // View state for toggling between form and list views
  const [domainViewMode, setDomainViewMode] = useState<'form' | 'list'>('form');
  const [questionFlowViewMode, setQuestionFlowViewMode] = useState<'form' | 'list'>('form');
  const [questionViewMode, setQuestionViewMode] = useState<'form' | 'list'>('form');
  const [scoringOptionViewMode, setScoringOptionViewMode] = useState<'form' | 'list'>('form');

  // Delete loading states
  const [deletingDomains, setDeletingDomains] = useState<number[]>([]);
  const [deletingQuestionFlows, setDeletingQuestionFlows] = useState<number[]>([]);
  const [deletingQuestions, setDeletingQuestions] = useState<number[]>([]);
  const [deletingScoringOptions, setDeletingScoringOptions] = useState<number[]>([]);

  // Drag and drop sensors for @dnd-kit
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Drag starts after moving 8px
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const steps = [
    { number: 1, title: 'Domains', description: 'Define domains for your framework' },
    { number: 2, title: 'Question Flows', description: 'Create question flows for domains' },
    { number: 3, title: 'Questions', description: 'Add questions to question flows' },
    { number: 4, title: 'Scoring Options', description: 'Define scoring options for the framework' }
  ];

  // Reset form data when framework changes
  useEffect(() => {
    if (frameworkData) {
      setDomainFormData(prev => ({ ...prev, category_id: frameworkData.id }));
      setScoringOptionFormData(prev => ({ ...prev, framework_id: frameworkData.id }));
    }
  }, [frameworkData]);

  // Table column definitions
  const domainColumns = [
    {
      accessorKey: "title",
      header: () => <span style={{ color: "#183FCE", fontSize: '12px', fontWeight: 'bold' }}>Title</span>,
      width: "25%",
      cell: ({ row }: { row: any }) => (
        <div style={{ fontSize: '0.875rem', fontFamily: 'Roboto' }}>
          {row.original.title || row.original.name || 'Unknown Title'}
        </div>
      ),
    },
    {
      accessorKey: "description",
      header: () => <span style={{ color: "#183FCE", fontSize: '12px', fontWeight: 'bold' }}>Shortcode</span>,
      width: "25%",
      cell: ({ row }: { row: any }) => (
        <div style={{ fontSize: '0.875rem', fontFamily: 'Roboto' }}>
          {row.original.description || row.original.shortcode || 'N/A'}
        </div>
      ),
    },
    {
      accessorKey: "is_enabled",
      header: () => <span style={{ color: "#183FCE", fontSize: '12px', fontWeight: 'bold' }}>Status</span>,
      width: "25%",
      cell: ({ row }: { row: any }) => (
        <div style={{ fontSize: '0.875rem', fontFamily: 'Roboto' }}>
          <span className={`badge ${(row.original.is_enabled ?? true) ? 'bg-success' : 'bg-secondary'} me-1`}>
            {(row.original.is_enabled ?? true) ? 'Enabled' : 'Disabled'}
          </span>
          <span className={`badge ${(row.original.is_active ?? true) ? 'bg-primary' : 'bg-warning'}`}>
            {(row.original.is_active ?? true) ? 'Active' : 'Inactive'}
          </span>
        </div>
      ),
    },
    {
      accessorKey: "category_id",
      header: () => <span style={{ color: "#183FCE", fontSize: '12px', fontWeight: 'bold' }}>Framework ID</span>,
      width: "15%",
      cell: ({ row }: { row: any }) => (
        <div style={{ fontSize: '0.875rem', fontFamily: 'Roboto' }}>
          {row.original.category_id || 'N/A'}
        </div>
      ),
    },
    {
      accessorKey: "actions",
      header: () => <span style={{ color: "#183FCE", fontSize: '12px', fontWeight: 'bold' }}>Actions</span>,
      width: "10%",
      cell: ({ row }: { row: any }) => {
        const domainId = row.original.id;
        const isDeleting = deletingDomains.includes(domainId);
        
        return (
          <div style={{ fontSize: '0.875rem', fontFamily: 'Roboto', textAlign: 'center' }}>
            <button
              onClick={() => handleDeleteDomain(domainId)}
              disabled={isDeleting}
              style={{
                background: 'none',
                border: 'none',
                cursor: isDeleting ? 'not-allowed' : 'pointer',
                padding: '4px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Delete domain"
            >
              {isDeleting ? (
                <div className="spinner-border spinner-border-sm text-danger" role="status" style={{ width: '16px', height: '16px' }}>
                  <span className="visually-hidden">Loading...</span>
                </div>
              ) : (
                <i className="bi bi-trash text-danger" style={{ fontSize: '16px' }}></i>
              )}
            </button>
          </div>
        );
      },
    },
  ];

  const questionFlowColumns = [
    {
      accessorKey: "title",
      header: () => <span style={{ color: "#183FCE", fontSize: '12px', fontWeight: 'bold' }}>Title</span>,
      width: "20%",
      cell: ({ row }: { row: any }) => (
        <div style={{ fontSize: '0.875rem', fontFamily: 'Roboto' }}>
          {row.original.title || row.original.name || 'Unknown Title'}
        </div>
      ),
    },
    {
      accessorKey: "description",
      header: () => <span style={{ color: "#183FCE", fontSize: '12px', fontWeight: 'bold' }}>Shortcode</span>,
      width: "15%",
      cell: ({ row }: { row: any }) => (
        <div style={{ fontSize: '0.875rem', fontFamily: 'Roboto' }}>
          {row.original.description || row.original.shortcode || 'N/A'}
        </div>
      ),
    },
    {
      accessorKey: "overall_score",
      header: () => <span style={{ color: "#183FCE", fontSize: '12px', fontWeight: 'bold' }}>Overall Score</span>,
      width: "15%",
      cell: ({ row }: { row: any }) => (
        <div style={{ fontSize: '0.875rem', fontFamily: 'Roboto' }}>
          {row.original.overall_score ?? 0}
        </div>
      ),
    },
    {
      accessorKey: "domain_id",
      header: () => <span style={{ color: "#183FCE", fontSize: '12px', fontWeight: 'bold' }}>Domain</span>,
      width: "20%",
      cell: ({ row }: { row: any }) => (
        <div style={{ fontSize: '0.875rem', fontFamily: 'Roboto' }}>
          {domains.find(d => d.id === row.original.domain_id)?.title || 'Unknown Domain'}
        </div>
      ),
    },
          {
        accessorKey: "status",
        header: () => <span style={{ color: "#183FCE", fontSize: '12px', fontWeight: 'bold' }}>Status</span>,
        width: "15%",
        cell: ({ row }: { row: any }) => (
          <div style={{ fontSize: '0.875rem', fontFamily: 'Roboto' }}>
            <span className={`badge ${(row.original.is_enabled ?? true) ? 'bg-success' : 'bg-secondary'} me-1`}>
              {(row.original.is_enabled ?? true) ? 'Enabled' : 'Disabled'}
            </span>
            <span className={`badge ${(row.original.is_deleted ?? false) ? 'bg-danger' : 'bg-primary'}`}>
              {(row.original.is_deleted ?? false) ? 'Deleted' : 'Active'}
            </span>
          </div>
        ),
      },
      {
        accessorKey: "actions",
        header: () => <span style={{ color: "#183FCE", fontSize: '12px', fontWeight: 'bold' }}>Actions</span>,
        width: "10%",
        cell: ({ row }: { row: any }) => {
          const questionFlowId = row.original.id;
          const isDeleting = deletingQuestionFlows.includes(questionFlowId);
          
          return (
            <div style={{ fontSize: '0.875rem', fontFamily: 'Roboto', textAlign: 'center' }}>
              <button
                onClick={() => handleDeleteQuestionFlow(questionFlowId)}
                disabled={isDeleting}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: isDeleting ? 'not-allowed' : 'pointer',
                  padding: '4px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Delete question flow"
              >
                {isDeleting ? (
                  <div className="spinner-border spinner-border-sm text-danger" role="status" style={{ width: '16px', height: '16px' }}>
                    <span className="visually-hidden">Loading...</span>
                  </div>
                ) : (
                  <i className="bi bi-trash text-danger" style={{ fontSize: '16px' }}></i>
                )}
              </button>
            </div>
          );
        },
      },
    ];

    // Group question flows by domain (exclude flows with is_deleted = true, but show flows even from inactive domains)
  const questionFlowsByDomain = domains.reduce((acc, domain) => {
      const domainFlows = questionFlows
        .filter(flow => flow.domain_id === domain.id && flow.is_deleted !== true)
        .sort((a, b) => (a.title || '').localeCompare(b.title || ''));
      
      if (domainFlows.length > 0) {
        acc[domain.id] = {
          domain,
          flows: domainFlows
        };
      }
      return acc;
    }, {} as { [key: number]: { domain: any; flows: any[] } });

  // Group questions by flow and sort by order within each flow (exclude flows with is_deleted = true)
  const questionsByFlow = questionFlows
    .filter(flow => flow.is_deleted !== true)
    .reduce((acc, flow) => {
      const flowQuestions = questions
        .filter(q => q.flow_id === flow.id)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      
      if (flowQuestions.length > 0) {
        acc[flow.id] = {
          flow,
          questions: flowQuestions
        };
      }
      return acc;
    }, {} as { [key: number]: { flow: any; questions: any[] } });

  // Sortable Item component using @dnd-kit for flow-specific questions
  const SortableItem = ({ question, index, flowId }: { question: any; index: number; flowId: number }) => {
    // Ensure we have a valid ID that's unique per flow
    const itemId = question.id || `question-${flowId}-${index}`;
    
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ 
      id: itemId,
      data: {
        type: 'question',
        question,
        index,
        flowId
      }
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.8 : 1,
      display: 'grid',
      gridTemplateColumns: '50px 80px 1fr 150px 60px', // Added space for delete column
      padding: '12px 8px',
      borderBottom: '1px solid #f0f0f0',
      fontSize: '0.875rem',
      fontFamily: 'Roboto',
      alignItems: 'center',
      backgroundColor: isDragging ? '#f8f9fa' : 'white',
      borderRadius: isDragging ? '8px' : '0',
      boxShadow: isDragging ? '0 5px 15px rgba(0,0,0,0.2)' : 'none',
      zIndex: isDragging ? 1000 : 'auto',
    };

    return (
      <div ref={setNodeRef} style={style} {...attributes}>
        {/* Drag handle - Only apply listeners here */}
        <div 
          {...listeners}
          style={{ 
            textAlign: 'center',
            cursor: 'grab',
            padding: '8px',
            borderRadius: '4px',
            backgroundColor: 'transparent',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            if (!isDragging) {
              e.currentTarget.style.backgroundColor = '#e3f2fd';
              e.currentTarget.style.cursor = 'grab';
            }
          }}
          onMouseLeave={(e) => {
            if (!isDragging) {
              e.currentTarget.style.backgroundColor = 'transparent';
            }
          }}
        >
          <i 
            className="bi bi-grip-vertical" 
            style={{ 
              color: isDragging ? '#183FCE' : '#6c757d', 
              fontSize: '16px',
              transition: 'color 0.2s ease',
              pointerEvents: 'none' // Prevent icon from interfering with drag
            }}
            title="Drag to reorder"
          />
        </div>
        
        {/* Order */}
        <div>
          <span className="badge bg-secondary">
            {question.order || index + 1}
          </span>
        </div>
        
        {/* Question text */}
        <div style={{ 
          overflow: 'hidden', 
          textOverflow: 'ellipsis', 
          whiteSpace: 'nowrap',
          paddingRight: '10px'
        }}>
          {question.question_text || 'Unknown Question'}
        </div>
        
        {/* Status */}
        <div>
          <span className={`badge ${(question.is_enabled ?? true) ? 'bg-success' : 'bg-secondary'} me-1`} style={{ fontSize: '10px' }}>
            {(question.is_enabled ?? true) ? 'Enabled' : 'Disabled'}
          </span>
          <span className={`badge ${(question.is_deleted ?? false) ? 'bg-danger' : 'bg-primary'}`} style={{ fontSize: '10px' }}>
            {(question.is_deleted ?? false) ? 'Deleted' : 'Active'}
          </span>
        </div>
        
        {/* Delete Actions */}
        <div style={{ textAlign: 'center' }}>
          {(() => {
            const questionId = question.id;
            const isDeleting = deletingQuestions.includes(questionId);
            
            return (
              <button
                onClick={() => handleDeleteQuestion(questionId)}
                disabled={isDeleting}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: isDeleting ? 'not-allowed' : 'pointer',
                  padding: '4px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Delete question"
              >
                {isDeleting ? (
                  <div className="spinner-border spinner-border-sm text-danger" role="status" style={{ width: '14px', height: '14px' }}>
                    <span className="visually-hidden">Loading...</span>
                  </div>
                ) : (
                  <i className="bi bi-trash text-danger" style={{ fontSize: '14px' }}></i>
                )}
              </button>
            );
          })()}
        </div>
      </div>
    );
  };

    // Domain-specific question flows component using AdminTables for consistency
  const DomainFlowsSection = ({ domainId, domainData }: { domainId: number; domainData: { domain: any; flows: any[] } }) => {
    const { domain, flows: domainFlows } = domainData;
    
    // Create columns specific to this domain's flows, including question count
    const domainFlowColumns = [
      {
        accessorKey: "title",
        header: () => <span style={{ color: "#183FCE", fontSize: '12px', fontWeight: 'bold' }}>Title</span>,
        width: "20%",
        cell: ({ row }: { row: any }) => (
          <div style={{ fontSize: '0.875rem', fontFamily: 'Roboto' }}>
            {row.original.title || row.original.name || 'Unknown Title'}
          </div>
        ),
      },
      {
        accessorKey: "description",
        header: () => <span style={{ color: "#183FCE", fontSize: '12px', fontWeight: 'bold' }}>Shortcode</span>,
        width: "20%",
        cell: ({ row }: { row: any }) => (
          <div style={{ fontSize: '0.875rem', fontFamily: 'Roboto' }}>
            {row.original.description || row.original.shortcode || 'N/A'}
          </div>
        ),
      },
      {
        accessorKey: "overall_score",
        header: () => <span style={{ color: "#183FCE", fontSize: '12px', fontWeight: 'bold' }}>Overall Score</span>,
        width: "15%",
        cell: ({ row }: { row: any }) => (
          <div style={{ fontSize: '0.875rem', fontFamily: 'Roboto' }}>
            {row.original.overall_score ?? 0}
          </div>
        ),
      },
      {
        accessorKey: "question_count",
        header: () => <span style={{ color: "#183FCE", fontSize: '12px', fontWeight: 'bold' }}>Questions</span>,
        width: "20%",
        cell: ({ row }: { row: any }) => {
          const questionCount = questions.filter(q => q.flow_id === row.original.id).length;
          return (
            <div style={{ fontSize: '0.875rem', fontFamily: 'Roboto', color: 'black' }}>
              {questionCount} question{questionCount !== 1 ? 's' : ''}
            </div>
          );
        },
      },
      {
        accessorKey: "status",
        header: () => <span style={{ color: "#183FCE", fontSize: '12px', fontWeight: 'bold' }}>Status</span>,
        width: "15%",
        cell: ({ row }: { row: any }) => (
          <div style={{ fontSize: '0.875rem', fontFamily: 'Roboto' }}>
            <span className={`badge ${(row.original.is_enabled ?? true) ? 'bg-success' : 'bg-secondary'} me-1`}>
              {(row.original.is_enabled ?? true) ? 'Enabled' : 'Disabled'}
            </span>
            <span className={`badge ${(row.original.is_deleted ?? false) ? 'bg-danger' : 'bg-primary'}`}>
              {(row.original.is_deleted ?? false) ? 'Deleted' : 'Active'}
            </span>
          </div>
        ),
      },
      {
        accessorKey: "actions",
        header: () => <span style={{ color: "#183FCE", fontSize: '12px', fontWeight: 'bold' }}>Actions</span>,
        width: "10%",
        cell: ({ row }: { row: any }) => {
          const questionFlowId = row.original.id;
          const isDeleting = deletingQuestionFlows.includes(questionFlowId);
          
          return (
            <div style={{ fontSize: '0.875rem', fontFamily: 'Roboto', textAlign: 'center' }}>
              <button
                onClick={() => handleDeleteQuestionFlow(questionFlowId)}
                disabled={isDeleting}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: isDeleting ? 'not-allowed' : 'pointer',
                  padding: '4px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Delete question flow"
              >
                {isDeleting ? (
                  <div className="spinner-border spinner-border-sm text-danger" role="status" style={{ width: '16px', height: '16px' }}>
                    <span className="visually-hidden">Loading...</span>
                  </div>
                ) : (
                  <i className="bi bi-trash text-danger" style={{ fontSize: '16px' }}></i>
                )}
              </button>
            </div>
          );
        },
      },
    ];
    
    return (
      <div style={{ 
        marginBottom: '24px', 
        border: '1px solid #dee2e6', 
        borderRadius: '8px', 
        backgroundColor: 'white',
        overflow: 'hidden'
      }}>
        {/* Domain Header */}
        <div style={{
          position: 'relative',
          color: 'white',
          padding: '12px 16px',
          fontSize: '14px',
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          overflow: 'hidden'
        }}>
          {/* Background Image Layer */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url(${bgPublic})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            zIndex: 1
          }}></div>
          
          {/* Gray Overlay Layer */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url(${grayBgTransparent})`,
            backgroundSize: 'cover',
            backgroundPosition: 'right',
            backgroundRepeat: 'no-repeat',
            zIndex: 2
          }}></div>
          
          {/* Content Layer */}
          <span style={{ position: 'relative', zIndex: 3 }}>
            {domain.title || domain.name || 'Unknown Domain'}
          </span>
          <span className="badge bg-light text-dark" style={{ position: 'relative', zIndex: 3 }}>
            {domainFlows.length} flow{domainFlows.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Question Flows Table */}
        <div style={{ padding: '0' }}>
          <AdminTables
            data={domainFlows}
            columns={domainFlowColumns}
            rowSelectionEnabled={false}
            onRowSelect={() => {}}
            showSearch={false}
            isLoading={false}
            containerStyle={{
              width: "100%",
              overflowX: "auto",
              border: "none", // Remove border since we have the parent container border
              borderRadius: "0" // Remove border radius for seamless integration
            }}
          />
        </div>
      </div>
    );
  };

  // Custom question flows table component organized by domain
  const DraggableQuestionFlowsTable = () => {
    // Debug: Check question flows data grouped by domain
    console.log('🔍 Question flows grouped by domain:', questionFlowsByDomain);

    const domainEntries = Object.entries(questionFlowsByDomain);

    // Note: We don't filter domains here since question flows should show even if domain is inactive
    // The filtering is already done in questionFlowsByDomain computation above
    const activeDomainEntries = domainEntries;

    if (activeDomainEntries.length === 0) {
      return (
        <div className="text-center text-muted py-4" style={{ 
          border: '1px solid #dee2e6', 
          borderRadius: '8px', 
          backgroundColor: 'white',
          padding: '40px'
        }}>
          <i className="bi bi-diagram-3" style={{ fontSize: '32px', color: '#6c757d' }}></i>
          <p className="mt-3 mb-0">No question flows available</p>
          <small className="text-muted">Add some question flows to see them organized by domain</small>
        </div>
      );
    }

    return (
      <div>
        {activeDomainEntries.map(([domainIdStr, domainData]) => {
          const domainId = parseInt(domainIdStr);
          const typedDomainData = domainData as { domain: any; flows: any[] };
          return (
            <DomainFlowsSection 
              key={domainId} 
              domainId={domainId} 
              domainData={typedDomainData} 
            />
          );
        })}
      </div>
    );
  };

  // Flow-specific draggable questions component
  const FlowQuestionsSection = ({ flowId, flowData }: { flowId: number; flowData: { flow: any; questions: any[] } }) => {
    const { flow, questions: flowQuestions } = flowData;
    
    return (
      <div style={{ 
        marginBottom: '24px', 
        border: '1px solid #dee2e6', 
        borderRadius: '8px', 
        backgroundColor: 'white',
        overflow: 'hidden'
      }}>
        {/* Flow Header */}
        <div style={{
          position: 'relative',
          color: 'white',
          padding: '12px 16px',
          fontSize: '14px',
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          overflow: 'hidden'
        }}>
          {/* Background Image Layer */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url(${bgPublic})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            zIndex: 1
          }}></div>
          
          {/* Gray Overlay Layer */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url(${grayBgTransparent})`,
            backgroundSize: 'cover',
            backgroundPosition: 'right',
            backgroundRepeat: 'no-repeat',
            zIndex: 2
          }}></div>
          
          {/* Content Layer */}
          <span style={{ position: 'relative', zIndex: 3 }}>
            {flow.title || 'Unknown Flow'}
          </span>
          <span className="badge bg-light text-dark" style={{ position: 'relative', zIndex: 3 }}>
            {flowQuestions.length} question{flowQuestions.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Questions Header */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '50px 80px 1fr 150px 60px',
          backgroundColor: '#f8f9fa', 
          borderBottom: '1px solid #dee2e6',
          fontSize: '12px',
          fontWeight: 'bold',
          color: '#183FCE',
          padding: '12px 8px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <i className="bi bi-arrows-move" title="Drag to reorder"></i>
          </div>
          <div>Order</div>
          <div>Question Text</div>
          <div>Status</div>
          <div style={{ textAlign: 'center' }}>Actions</div>
        </div>

        {/* Drag and Drop Context for this flow */}
        <div style={{ maxHeight: '300px', overflow: 'auto' }}>
          <DndContext 
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={(event) => {
              console.log('🟢 Drag started in flow:', flow.title, event.active.id);
            }}
            onDragEnd={handleFlowDragEnd(flowId)}
          >
            <SortableContext 
              items={flowQuestions.map((q, idx) => q.id || `question-${flowId}-${idx}`)}
              strategy={verticalListSortingStrategy}
            >
              {flowQuestions.map((question, index) => (
                <SortableItem 
                  key={question.id || `question-${flowId}-${index}`} 
                  question={question} 
                  index={index}
                  flowId={flowId}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      </div>
    );
  };

  // Custom draggable questions table component using @dnd-kit
  const DraggableQuestionsTable = () => {
    // Debug: Check questions data grouped by flow
    console.log('🔍 Questions grouped by flow:', questionsByFlow);

    const flowEntries = Object.entries(questionsByFlow);

    if (flowEntries.length === 0) {
      return (
        <div className="text-center text-muted py-4" style={{ 
          border: '1px solid #dee2e6', 
          borderRadius: '8px', 
          backgroundColor: 'white',
          padding: '40px'
        }}>
          <i className="bi bi-question-circle" style={{ fontSize: '32px', color: '#6c757d' }}></i>
          <p className="mt-3 mb-0">No questions available</p>
          <small className="text-muted">Add some questions to see them organized by flow</small>
        </div>
      );
    }

    return (
      <div>
                 {flowEntries.map(([flowIdStr, flowData]) => {
           const flowId = parseInt(flowIdStr);
           const typedFlowData = flowData as { flow: any; questions: any[] };
           return (
             <FlowQuestionsSection 
               key={flowId} 
               flowId={flowId} 
               flowData={typedFlowData} 
             />
           );
         })}
      </div>
    );
  };

  const questionColumns = [
    {
      accessorKey: "question_text",
      header: () => <span style={{ color: "#183FCE", fontSize: '12px', fontWeight: 'bold' }}>Question Text</span>,
      width: "40%",
      cell: ({ row }: { row: any }) => (
        <div style={{ fontSize: '0.875rem', fontFamily: 'Roboto', maxWidth: '300px' }}>
          {row.original.question_text || 'Unknown Question'}
        </div>
      ),
    },
    {
      accessorKey: "order",
      header: () => <span style={{ color: "#183FCE", fontSize: '12px', fontWeight: 'bold' }}>Order</span>,
      width: "10%",
      cell: ({ row }: { row: any }) => (
        <div style={{ fontSize: '0.875rem', fontFamily: 'Roboto' }}>
          {row.original.order || 'N/A'}
        </div>
      ),
    },
    {
      accessorKey: "flow_id",
      header: () => <span style={{ color: "#183FCE", fontSize: '12px', fontWeight: 'bold' }}>Question Flow</span>,
      width: "25%",
      cell: ({ row }: { row: any }) => (
        <div style={{ fontSize: '0.875rem', fontFamily: 'Roboto' }}>
          {questionFlows.find(f => f.id === row.original.flow_id)?.title || 'Unknown Flow'}
        </div>
      ),
    },
    {
      accessorKey: "status",
      header: () => <span style={{ color: "#183FCE", fontSize: '12px', fontWeight: 'bold' }}>Status</span>,
      width: "25%",
      cell: ({ row }: { row: any }) => (
        <div style={{ fontSize: '0.875rem', fontFamily: 'Roboto' }}>
          <span className={`badge ${(row.original.is_enabled ?? true) ? 'bg-success' : 'bg-secondary'} me-1`}>
            {(row.original.is_enabled ?? true) ? 'Enabled' : 'Disabled'}
          </span>
          <span className={`badge ${(row.original.is_deleted ?? false) ? 'bg-danger' : 'bg-primary'}`}>
            {(row.original.is_deleted ?? false) ? 'Deleted' : 'Active'}
          </span>
        </div>
      ),
    },
  ];

  // Scoring options columns
  const scoringOptionColumns = [
    {
      accessorKey: "option_name",
      header: () => <span style={{ color: "#183FCE", fontSize: '12px', fontWeight: 'bold' }}>Option Name</span>,
      width: "25%",
      cell: ({ row }: { row: any }) => (
        <div style={{ fontSize: '0.875rem', fontFamily: 'Roboto' }}>
          {row.original.option_name || 'Unknown Option'}
        </div>
      ),
    },
    {
      accessorKey: "score",
      header: () => <span style={{ color: "#183FCE", fontSize: '12px', fontWeight: 'bold' }}>Score</span>,
      width: "15%",
      cell: ({ row }: { row: any }) => (
        <div style={{ fontSize: '0.875rem', fontFamily: 'Roboto' }}>
          <span className="">
            {row.original.score ?? 0}
          </span>
        </div>
      ),
    },
    {
      accessorKey: "tooltip_content",
      header: () => <span style={{ color: "#183FCE", fontSize: '12px', fontWeight: 'bold' }}>Tooltip Keys</span>,
      width: "25%",
      cell: ({ row }: { row: any }) => {
        const tooltipContent = row.original.tooltip_content || {};
        const keys = Object.keys(tooltipContent);
        return (
          <div style={{ fontSize: '0.875rem', fontFamily: 'Roboto' }}>
            {keys.length > 0 ? (
              <div>
                {keys.slice(0, 2).map((key, idx) => (
                  <span key={idx} className="text-muted" style={{ fontSize: '0.875rem' }}>
                    {key}
                  </span>
                ))}
                {keys.length > 2 && (
                  <span className="badge bg-light text-dark" style={{ fontSize: '0.875rem' }}>
                    +{keys.length - 2} more
                  </span>
                )}
              </div>
            ) : (
              <span className="text-muted">No tooltips</span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "status",
      header: () => <span style={{ color: "#183FCE", fontSize: '12px', fontWeight: 'bold' }}>Status</span>,
      width: "25%",
      cell: ({ row }: { row: any }) => (
        <div style={{ fontSize: '0.875rem', fontFamily: 'Roboto' }}>
          <span className={`badge ${(row.original.is_enabled ?? true) ? 'bg-success' : 'bg-secondary'} me-1`}>
            {(row.original.is_enabled ?? true) ? 'Enabled' : 'Disabled'}
          </span>
          <span className={`badge ${(row.original.is_deleted ?? false) ? 'bg-danger' : 'bg-primary'}`}>
            {(row.original.is_deleted ?? false) ? 'Deleted' : 'Active'}
          </span>
        </div>
      ),
    },
    {
      accessorKey: "actions",
      header: () => <span style={{ color: "#183FCE", fontSize: '12px', fontWeight: 'bold' }}>Actions</span>,
      width: "10%",
      cell: ({ row }: { row: any }) => {
        const scoringOptionId = row.original.id;
        const isDeleting = deletingScoringOptions.includes(scoringOptionId);
        
        return (
          <div style={{ fontSize: '0.875rem', fontFamily: 'Roboto', textAlign: 'center' }}>
            <button
              onClick={() => handleDeleteScoringOption(scoringOptionId)}
              disabled={isDeleting}
              style={{
                background: 'none',
                border: 'none',
                cursor: isDeleting ? 'not-allowed' : 'pointer',
                padding: '4px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Delete scoring option"
            >
              {isDeleting ? (
                <div className="spinner-border spinner-border-sm text-danger" role="status" style={{ width: '16px', height: '16px' }}>
                  <span className="visually-hidden">Loading...</span>
                </div>
              ) : (
                <i className="bi bi-trash text-danger" style={{ fontSize: '16px' }}></i>
              )}
            </button>
          </div>
        );
      },
    },
  ];

  // Domain form validation
  const validateDomainForm = () => {
    const errors: { [key: string]: string } = {};
    if (!domainFormData.category_id || domainFormData.category_id === 0) {
      errors.category_id = 'Category ID is required';
    }
    if (!domainFormData.title.trim()) {
      errors.title = 'Title is required';
    }
    if (!domainFormData.description.trim()) {
      errors.description = 'Shortcode is required';
    }
    setDomainErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Question Flow form validation
  const validateQuestionFlowForm = () => {
    const errors: { [key: string]: string } = {};
    if (!questionFlowFormData.title.trim()) {
      errors.title = 'Title is required';
    }
    if (!questionFlowFormData.description.trim()) {
      errors.description = 'Shortcode is required';
    }
    if (!questionFlowFormData.domain_id) {
      errors.domain_id = 'Please select a domain';
    } else {
      // Check if the selected domain_id actually exists in our domains list
      const selectedDomain = domains.find(d => d.id == questionFlowFormData.domain_id); // Use == for flexible comparison
      if (!selectedDomain) {
        errors.domain_id = 'Selected domain is invalid. Please select a valid domain.';
        console.error('Invalid domain_id selected:', questionFlowFormData.domain_id);
        console.error('Selected domain_id type:', typeof questionFlowFormData.domain_id);
        console.error('Available domains:', domains);
        console.error('Available domain IDs:', domains.map(d => ({ id: d.id, type: typeof d.id })));
      }
    }
    setQuestionFlowErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Question form validation
  const validateQuestionForm = () => {
    const errors: { [key: string]: string } = {};
    if (!questionFormData.question_text.trim()) {
      errors.question_text = 'Question text is required';
    }
    if (!questionFormData.flow_id) {
      errors.flow_id = 'Please select a question flow';
    }
    if (!questionFormData.order || questionFormData.order < 1) {
      errors.order = 'Order must be a positive number';
    }
    setQuestionErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Scoring option form validation
  const validateScoringOptionForm = () => {
    const errors: { [key: string]: string } = {};
    if (!scoringOptionFormData.framework_id || scoringOptionFormData.framework_id === 0) {
      errors.framework_id = 'Framework ID is required';
    }
    if (!scoringOptionFormData.option_name.trim()) {
      errors.option_name = 'Option name is required';
    }
    if (scoringOptionFormData.score < 0) {
      errors.score = 'Score must be a non-negative number';
    }
    setScoringOptionErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle domain form changes
  const handleDomainChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setDomainFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (domainErrors[name]) {
      setDomainErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Handle question flow form changes
  const handleQuestionFlowChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    console.log('Question flow change:', { name, value, type });
    
    const newValue = type === 'checkbox' ? checked : (name === 'domain_id' ? parseInt(value) : value);
    
    setQuestionFlowFormData(prev => {
      const updated = {
      ...prev,
        [name]: newValue
      };
      console.log('Updated question flow form data:', updated);
      return updated;
    });
    
    if (questionFlowErrors[name]) {
      setQuestionFlowErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Handle question form changes
  const handleQuestionChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setQuestionFormData(prev => {
      const updatedData = {
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'flow_id' || name === 'order' ? parseInt(value) : value)
      };
      
      // Reset order to 1 when question flow changes
      if (name === 'flow_id' && parseInt(value) !== prev.flow_id) {
        updatedData.order = 1;
        console.log('🔄 Question flow changed, resetting order to 1');
      }
      
      return updatedData;
    });
    
    if (questionErrors[name]) {
      setQuestionErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Handle scoring option form changes
  const handleScoringOptionChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setScoringOptionFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'score' || name === 'framework_id' ? parseInt(value) : value)
    }));
    
    if (scoringOptionErrors[name]) {
      setScoringOptionErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Handle tooltip content changes
  const handleTooltipChange = (keyIndex: number, field: 'key' | 'value', value: string, valueIndex?: number) => {
    const updatedKeys = [...tooltipKeys];
    const updatedTooltipContent = { ...scoringOptionFormData.tooltip_content };
    
    if (field === 'key') {
      // Update key name
      const oldKey = updatedKeys[keyIndex];
      updatedKeys[keyIndex] = value;
      
      if (oldKey && oldKey !== value) {
        // Move values from old key to new key
        updatedTooltipContent[value] = updatedTooltipContent[oldKey] || [''];
        delete updatedTooltipContent[oldKey];
      } else if (!updatedTooltipContent[value]) {
        updatedTooltipContent[value] = [''];
      }
    } else if (field === 'value' && valueIndex !== undefined) {
      // Update individual value
      const key = updatedKeys[keyIndex];
      if (key) {
        if (!updatedTooltipContent[key]) {
          updatedTooltipContent[key] = [''];
        }
        updatedTooltipContent[key][valueIndex] = value;
      }
    }
    
    setTooltipKeys(updatedKeys);
    setScoringOptionFormData(prev => ({
      ...prev,
      tooltip_content: updatedTooltipContent
    }));
  };

  // Add new tooltip key
  const addTooltipKey = () => {
    setTooltipKeys(prev => [...prev, '']);
  };

  // Remove tooltip key
  const removeTooltipKey = (index: number) => {
    const keyToRemove = tooltipKeys[index];
    const updatedKeys = tooltipKeys.filter((_, i) => i !== index);
    const updatedTooltipContent = { ...scoringOptionFormData.tooltip_content };
    
    if (keyToRemove) {
      delete updatedTooltipContent[keyToRemove];
    }
    
    setTooltipKeys(updatedKeys);
    setScoringOptionFormData(prev => ({
      ...prev,
      tooltip_content: updatedTooltipContent
    }));
  };

  // Add new tooltip value to a specific key
  const addTooltipValue = (keyIndex: number) => {
    const key = tooltipKeys[keyIndex];
    if (key) {
      const updatedTooltipContent = { ...scoringOptionFormData.tooltip_content };
      if (!updatedTooltipContent[key]) {
        updatedTooltipContent[key] = [''];
      } else {
        updatedTooltipContent[key] = [...updatedTooltipContent[key], ''];
      }
      
      setScoringOptionFormData(prev => ({
        ...prev,
        tooltip_content: updatedTooltipContent
      }));
    }
  };

  // Remove tooltip value from a specific key
  const removeTooltipValue = (keyIndex: number, valueIndex: number) => {
    const key = tooltipKeys[keyIndex];
    if (key) {
      const updatedTooltipContent = { ...scoringOptionFormData.tooltip_content };
      if (updatedTooltipContent[key] && updatedTooltipContent[key].length > 1) {
        updatedTooltipContent[key] = updatedTooltipContent[key].filter((_, i) => i !== valueIndex);
      }
      
      setScoringOptionFormData(prev => ({
        ...prev,
        tooltip_content: updatedTooltipContent
      }));
    }
  };

  // Add domain
  const handleAddDomain = async () => {
    if (!validateDomainForm()) return;
    
    setLoading(true);
    try {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.post(create_domain, domainFormData);
      
      if (response.status === 200 || response.status === 201) {
        // Log the response to debug structure
        console.log('Domain creation response:', response.data);
        
        // Create domain object with form data + response data
        const newDomain = {
          ...response.data?.data,
        };
        
        console.log('New domain created:', newDomain);
        
        setDomains(prev => [...prev, newDomain]);
        setDomainFormData({
          category_id: frameworkData?.id || 0,
          title: '',
          description: '',
          is_enabled: true,
          is_active: true
        });
        setAlert({
          message: 'Domain added successfully!',
          type: 'success',
          show: true
        });
      }
    } catch (error) {
      console.error('Error adding domain:', error);
      setAlert({
        message: 'Error adding domain. Please try again.',
        type: 'danger',
        show: true
      });
    } finally {
      setLoading(false);
    }
  };

  // Add question flow
  const handleAddQuestionFlow = async () => {
    if (!validateQuestionFlowForm()) return;
    
    setLoading(true);
    try {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.post(create_question_flow, questionFlowFormData);
      
      if (response.status === 200 || response.status === 201) {
        // Log the response to debug structure
        console.log('Question flow creation response:', response.data);
        
        // Create question flow object with form data + response data
        const newQuestionFlow = {
          ...response.data?.data,
        };
        
        setQuestionFlows(prev => [...prev, newQuestionFlow]);
        setQuestionFlowFormData({
          domain_id: 0,
          title: '',
          description: '',
          overall_score: 4,
          is_enabled: true,
          is_deleted: false
        });
        setAlert({
          message: 'Question flow added successfully!',
          type: 'success',
          show: true
        });
      }
    } catch (error) {
      console.error('Error adding question flow:', error);
      setAlert({
        message: 'Error adding question flow. Please try again.',
        type: 'danger',
        show: true
      });
    } finally {
      setLoading(false);
    }
  };

  // Add question
  const handleAddQuestion = async () => {
    if (!validateQuestionForm()) return;
    
    setLoading(true);
    try {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.post(create_question, questionFormData);
      
      if (response.status === 200 || response.status === 201) {
        // Log the response to debug structure
        console.log('Question creation response:', response.data);
        
        // Create question object with form data + response data
        console.log('📥 Question creation response:', response.data);
        
        const newQuestion = {
          ...questionFormData,
          ...response.data,
          // Ensure we have the form data values as fallback
          question_text: response.data.question_text || questionFormData.question_text,
          order: response.data.order || questionFormData.order,
          is_enabled: response.data.is_enabled ?? questionFormData.is_enabled,
          is_deleted: response.data.is_deleted ?? questionFormData.is_deleted,
          flow_id: response.data?.data?.flow_id || questionFormData.flow_id,
          // Try to get ID from different possible response structures
          id: response.data.id || response.data?.data?.id || response.data.pk || response.data?.data?.pk || response.data.ID || response.data?.data?.ID
        };
        
        console.log('✅ New question object:', newQuestion);
        
        setQuestions(prev => [...prev, newQuestion]);
        
        // Calculate next order number for the current flow
        const currentFlowQuestions = questions.filter(q => q.flow_id === questionFormData.flow_id);
        const nextOrder = currentFlowQuestions.length + 2; // +2 because we just added one
        
        setQuestionFormData(prev => ({
          flow_id: prev.flow_id, // Keep the same flow_id selected
          question_text: '',
          order: nextOrder,
          is_enabled: true,
          is_deleted: false
        }));
        
        console.log('✅ Question added, keeping flow_id:', questionFormData.flow_id, 'next order:', nextOrder);
        setAlert({
          message: 'Question added successfully!',
          type: 'success',
          show: true
        });
      }
    } catch (error) {
      console.error('Error adding question:', error);
      setAlert({
        message: 'Error adding question. Please try again.',
        type: 'danger',
        show: true
      });
    } finally {
      setLoading(false);
    }
  };

  // Add scoring option
  const handleAddScoringOption = async () => {
    if (!validateScoringOptionForm()) return;
    
    setLoading(true);
    try {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.post(create_scoring_option, scoringOptionFormData);
      
      if (response.status === 200 || response.status === 201) {
        // Log the response to debug structure
        console.log('📥 Scoring option creation response:', response.data);
        
        // Create scoring option object with form data + response data
        const newScoringOption = {
          ...scoringOptionFormData,
          ...response.data,
          // Ensure we have the form data values as fallback
          option_name: response.data.option_name || scoringOptionFormData.option_name,
          score: response.data.score ?? scoringOptionFormData.score,
          tooltip_content: response.data.tooltip_content || scoringOptionFormData.tooltip_content,
          is_enabled: response.data.is_enabled ?? scoringOptionFormData.is_enabled,
          is_deleted: response.data.is_deleted ?? scoringOptionFormData.is_deleted,
          framework_id: response.data?.data?.framework_id || scoringOptionFormData.framework_id,
          // Try to get ID from different possible response structures
          id: response.data.id || response.data?.data?.id || response.data.pk || response.data?.data?.pk || response.data.ID || response.data?.data?.ID
        };
        
        console.log('✅ New scoring option object:', newScoringOption);
        
        setScoringOptions(prev => [...prev, newScoringOption]);
        setScoringOptionFormData({
          framework_id: frameworkData?.id || 0,
          option_name: '',
          score: scoringOptions.length + 1, // Next score number
          tooltip_content: {},
          is_enabled: true,
          is_deleted: false
        });
        // Reset tooltip keys
        setTooltipKeys(['']);
        setAlert({
          message: 'Scoring option added successfully!',
          type: 'success',
          show: true
        });
      }
    } catch (error) {
      console.error('Error adding scoring option:', error);
      setAlert({
        message: 'Error adding scoring option. Please try again.',
        type: 'danger',
        show: true
      });
    } finally {
      setLoading(false);
    }
  };

  // Delete domain function
  const handleDeleteDomain = async (domainId: number) => {
    // Add to loading state
    setDeletingDomains(prev => [...prev, domainId]);
    
    try {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.delete(delete_domain(domainId));
      
      if (response.status === 200 || response.status === 204) {
        // Remove from local state
        setDomains(prev => prev.filter(domain => domain.id !== domainId));
        setAlert({
          message: 'Domain deleted successfully!',
          type: 'success',
          show: true
        });
        console.log('✅ Domain deleted:', domainId);
      }
    } catch (error) {
      console.error('Error deleting domain:', error);
      setAlert({
        message: 'Error deleting domain. Please try again.',
        type: 'danger',
        show: true
      });
    } finally {
      // Remove from loading state
      setDeletingDomains(prev => prev.filter(id => id !== domainId));
    }
  };

  // Delete question flow function
  const handleDeleteQuestionFlow = async (questionFlowId: number) => {
    // Add to loading state
    setDeletingQuestionFlows(prev => [...prev, questionFlowId]);
    
    try {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.delete(delete_question_flow(questionFlowId));
      
      if (response.status === 200 || response.status === 204) {
        // Remove from local state
        setQuestionFlows(prev => prev.filter(flow => flow.id !== questionFlowId));
        setAlert({
          message: 'Question flow deleted successfully!',
          type: 'success',
          show: true
        });
        console.log('✅ Question flow deleted:', questionFlowId);
      }
    } catch (error) {
      console.error('Error deleting question flow:', error);
      setAlert({
        message: 'Error deleting question flow. Please try again.',
        type: 'danger',
        show: true
      });
    } finally {
      // Remove from loading state
      setDeletingQuestionFlows(prev => prev.filter(id => id !== questionFlowId));
    }
  };

  // Delete question function
  const handleDeleteQuestion = async (questionId: number) => {
    // Add to loading state
    setDeletingQuestions(prev => [...prev, questionId]);
    
    try {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.delete(delete_question(questionId));
      
      if (response.status === 200 || response.status === 204) {
        // Remove from local state
        setQuestions(prev => prev.filter(question => question.id !== questionId));
        setAlert({
          message: 'Question deleted successfully!',
          type: 'success',
          show: true
        });
        console.log('✅ Question deleted:', questionId);
      }
    } catch (error) {
      console.error('Error deleting question:', error);
      setAlert({
        message: 'Error deleting question. Please try again.',
        type: 'danger',
        show: true
      });
    } finally {
      // Remove from loading state
      setDeletingQuestions(prev => prev.filter(id => id !== questionId));
    }
  };

  // Delete scoring option function
  const handleDeleteScoringOption = async (scoringOptionId: number) => {
    // Add to loading state
    setDeletingScoringOptions(prev => [...prev, scoringOptionId]);
    
    try {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.delete(delete_scoring_option(scoringOptionId));
      
      if (response.status === 200 || response.status === 204) {
        // Remove from local state
        setScoringOptions(prev => prev.filter(option => option.id !== scoringOptionId));
        setAlert({
          message: 'Scoring option deleted successfully!',
          type: 'success',
          show: true
        });
        console.log('✅ Scoring option deleted:', scoringOptionId);
      }
    } catch (error) {
      console.error('Error deleting scoring option:', error);
      setAlert({
        message: 'Error deleting scoring option. Please try again.',
        type: 'danger',
        show: true
      });
    } finally {
      // Remove from loading state
      setDeletingScoringOptions(prev => prev.filter(id => id !== scoringOptionId));
    }
  };

  // Update question function
  const handleUpdateQuestion = async (questionId: number, updatedData: any) => {
    try {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.put(update_question(questionId), updatedData);
      
      if (response.status === 200 || response.status === 201) {
        console.log('Question updated successfully:', response.data);
        return true;
      }
    } catch (error) {
      console.error('Error updating question:', error);
      setAlert({
        message: 'Error updating question order. Please try again.',
        type: 'danger',
        show: true
      });
      return false;
    }
  };

  // Handle drag end event from @dnd-kit for flow-specific reordering
  const handleFlowDragEnd = (flowId: number) => async (event: DragEndEvent) => {
    const { active, over } = event;

    console.log('🔴 Drag ended in flow:', flowId, { activeId: active.id, overId: over?.id });

    if (!over || active.id === over.id) {
      console.log('❌ No valid drop target or same position');
      return; // No change needed
    }

    const flowData = questionsByFlow[flowId];
    if (!flowData) {
      console.error('❌ Flow data not found');
      return;
    }

    const flowQuestions = flowData.questions;

    // Find indices using both real ID and fallback ID
    const oldIndex = flowQuestions.findIndex((question: any, idx: number) => 
      question.id === active.id || `question-${flowId}-${idx}` === active.id
    );
    const newIndex = flowQuestions.findIndex((question: any, idx: number) => 
      question.id === over.id || `question-${flowId}-${idx}` === over.id
    );

    console.log('📍 Found indices in flow:', { flowId, oldIndex, newIndex });

    if (oldIndex === -1 || newIndex === -1) {
      console.error('❌ Invalid indices found');
      return; // Invalid indices
    }

    console.log('🎯 Drag & Drop:', { 
      flowId,
      flowTitle: flowData.flow.title,
      from: oldIndex, 
      to: newIndex, 
      question: flowQuestions[oldIndex].question_text?.substring(0, 30) + '...' 
    });

    // Reorder the questions array within this flow
    const newFlowQuestions = arrayMove(flowQuestions, oldIndex, newIndex) as any[];

    // Update order values and make API calls
    const updatePromises = newFlowQuestions.map(async (question: any, index: number) => {
      const newOrder = index + 1;
      if (question.order !== newOrder) {
        // Ensure we have a valid question ID
        const questionId = question.id || question.pk || question.ID;
        
        if (!questionId) {
          console.error('❌ No valid question ID found for update:', question);
          return false;
        }

        const updatedQuestionData = {
          flow_id: question.flow_id,
          question_text: question.question_text,
          order: newOrder,
          is_enabled: question.is_enabled ?? true,
          is_deleted: question.is_deleted ?? false
        };

        console.log('📤 Updating question:', { questionId, newOrder, oldOrder: question.order });
        
        const success = await handleUpdateQuestion(questionId, updatedQuestionData);
        if (success) {
          // Update the question in the main questions array
          const questionIndex = questions.findIndex((q: any) => q.id === questionId || q.pk === questionId || q.ID === questionId);
          if (questionIndex !== -1) {
            questions[questionIndex].order = newOrder;
          }
        }
        return success;
      }
      return true;
    });

    // Update local state immediately for better UX
    setQuestions([...questions]);

    try {
      const results = await Promise.all(updatePromises);
      const allSuccessful = results.every(result => result);
      
      if (allSuccessful) {
        setAlert({
          message: `Questions in "${flowData.flow.title}" reordered successfully!`,
          type: 'success',
          show: true
        });
      } else {
        setAlert({
          message: 'Some questions failed to update. Please refresh and try again.',
          type: 'warning',
          show: true
        });
      }
    } catch (error) {
      console.error('Error updating question orders:', error);
      setAlert({
        message: 'Error reordering questions. Please refresh and try again.',
        type: 'danger',
        show: true
      });
    }
  };

  // Navigation handlers
  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleClose = () => {
    setCurrentStep(1);
    setDomains([]);
    setQuestionFlows([]);
    setQuestions([]);
    setScoringOptions([]);
    // Reset view modes to form view
    setDomainViewMode('form');
    setQuestionFlowViewMode('form');
    setQuestionViewMode('form');
    setScoringOptionViewMode('form');
    // Reset delete loading states
    setDeletingDomains([]);
    setDeletingQuestionFlows([]);
    setDeletingQuestions([]);
    setDeletingScoringOptions([]);
    // Reset tooltip keys
    setTooltipKeys(['']);
    onClose();
  };

  // Render domain form
  const renderDomainForm = () => (
    <div className="mb-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0" style={{ color: '#183FCE', fontWeight: 'bold' }}>
        Add New Domain
      </h6>
        <button
          type="button"
          className="btn btn-link p-0"
          onClick={() => setDomainViewMode(domainViewMode === 'form' ? 'list' : 'form')}
          style={{ 
            color: '#183FCE', 
            textDecoration: 'none',
            fontSize: '12px',
            fontWeight: '500'
          }}
        >
          {domainViewMode === 'form' ? 'View Added Domains' : 'Back to Domain Form'}
        </button>
             </div>

      {domainViewMode === 'form' ? (
        <>
              <div className="mb-3">
          <label className="form-label">Framework:</label>
          <div className={`form-control bg-light ${domainErrors.category_id ? 'is-invalid' : ''}`} style={{ backgroundColor: '#f8f9fa' }}>
            {frameworkData?.title || `Loading framework... (ID: ${domainFormData.category_id})`}
          </div>
          {domainErrors.category_id && (
            <div className="invalid-feedback">{domainErrors.category_id}</div>
          )}
          {frameworkData && (
            <small className="text-muted">Framework ID: {frameworkData.id}</small>
          )}
        </div>
      
      <div className="mb-3">
        <label htmlFor="domainTitle" className="form-label">Title:</label>
        <input
          type="text"
          className={`form-control bg-white ${domainErrors.title ? 'is-invalid' : ''}`}
          id="domainTitle"
          name="title"
          value={domainFormData.title}
          onChange={handleDomainChange}
          disabled={loading}
        />
        {domainErrors.title && (
          <div className="invalid-feedback">{domainErrors.title}</div>
        )}
      </div>
      
      <div className="mb-3">
        <label htmlFor="domainDescription" className="form-label">Shortcode:</label>
        <input
          type="text"
          className={`form-control bg-white ${domainErrors.description ? 'is-invalid' : ''}`}
          id="domainDescription"
          name="description"
          value={domainFormData.description}
          onChange={handleDomainChange}
          disabled={loading}
        />
        {domainErrors.description && (
          <div className="invalid-feedback d-block text-danger" style={{fontSize: '1rem'}}>{domainErrors.description}</div>
        )}
      </div>
      
      <div className="mb-3">
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id="domainEnabled"
            name="is_enabled"
            checked={domainFormData.is_enabled}
            onChange={handleDomainChange}
            disabled={loading}
          />
          <label className="form-check-label" htmlFor="domainEnabled">
            Enable Domain
          </label>
        </div>
      </div>
      
      <div className="mb-3">
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id="domainActive"
            name="is_active"
            checked={domainFormData.is_active}
            onChange={handleDomainChange}
            disabled={loading}
          />
          <label className="form-check-label" htmlFor="domainActive">
            Mark as Active
          </label>
        </div>
      </div>
      
      <button
        type="button"
        className="btn btn-rounded"
        onClick={handleAddDomain}
        disabled={loading}
        style={{
          background: '#26397E',
          borderRadius: "77.125rem",
          transition: 'all 0.3s ease',
        }}
        onMouseEnter={(e) => {
          const target = e.target as HTMLButtonElement;
          target.style.backgroundColor = '#5376F0 !important';
          target.style.transform = 'translateY(-2px)';
        }}
        onMouseLeave={(e) => {
          const target = e.target as HTMLButtonElement;
          target.style.backgroundColor = '#26397E';
          target.style.transform = 'translateY(0)';
        }}
      >
        {loading ? (
          <div className="d-flex align-items-center">
            <div className="spinner-border spinner-border-sm me-2" role="status"></div>
            Adding...
          </div>
        ) : (
          'Add Domain'
        )}
      </button>
        </>
      ) : (
                // List View
        <div className="mt-3">
          <div style={{ 
            marginBottom: '24px', 
            border: '1px solid #dee2e6', 
            borderRadius: '8px', 
            backgroundColor: 'white',
            overflow: 'hidden'
          }}>
            {/* Header with background images */}
            <div style={{
              position: 'relative',
              color: 'white',
              padding: '12px 16px',
              fontSize: '14px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              overflow: 'hidden'
            }}>
              {/* Background Image Layer */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `url(${bgPublic})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                zIndex: 1
              }}></div>
              
              {/* Gray Overlay Layer */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `url(${grayBgTransparent})`,
                backgroundSize: 'cover',
                backgroundPosition: 'right',
                backgroundRepeat: 'no-repeat',
                zIndex: 2
              }}></div>
              
              {/* Content Layer */}
              <span style={{ position: 'relative', zIndex: 3 }}>
                {frameworkData?.title || 'Framework Domains'}
              </span>
              <span className="badge bg-light text-dark" style={{ position: 'relative', zIndex: 3 }}>
                {domains.length} domain{domains.length !== 1 ? 's' : ''}
              </span>
            </div>

            {/* Table Content */}
            {(() => {
              // Filter out domains with is_active = false
              const activeDomains = domains.filter(domain => domain.is_active !== false);
              return activeDomains.length > 0 ? (
                <div>
                  <AdminTables
                    data={activeDomains}
                    columns={domainColumns}
                    rowSelectionEnabled={false}
                    onRowSelect={() => {}}
                    showSearch={false}
                    isLoading={false}
                    containerStyle={{
                      width: "100%",
                      overflowX: "auto"
                    }}
                  />
                </div>
              ) : (
                <div className="text-center text-muted py-4">
                  <p>No active domains available.</p>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => setDomainViewMode('form')}
                  >
                    Add Your First Domain
                  </button>
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );

  // Render question flow form
  const renderQuestionFlowForm = () => {
    console.log('Rendering question flow form. Available domains:', domains);
    return (
    <div className="mb-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0" style={{ color: '#183FCE', fontWeight: 'bold' }}>
        Add New Question Flow
      </h6>
        <button
          type="button"
          className="btn btn-link p-0"
          onClick={() => setQuestionFlowViewMode(questionFlowViewMode === 'form' ? 'list' : 'form')}
          style={{ 
            color: '#183FCE', 
            textDecoration: 'none',
            fontSize: '12px',
            fontWeight: '500'
          }}
        >
          {questionFlowViewMode === 'form' ? 'View Added Question Flows' : 'Back to Question Flow Form'}
        </button>
      </div>

      {questionFlowViewMode === 'form' ? (
        <>
      
      <div className="mb-3">
        <label htmlFor="flowDomain" className="form-label">Select Domain:</label>
        <select
          className={`form-select bg-white ${questionFlowErrors.domain_id ? 'is-invalid' : ''}`}
          id="flowDomain"
          name="domain_id"
          value={questionFlowFormData.domain_id}
          onChange={handleQuestionFlowChange}
          disabled={loading}
        >
          <option value={0}>Select a domain</option>
          {domains
            .filter(domain => domain.is_active !== false)
            .map((domain, index) => {
              console.log('Domain for dropdown:', domain);
              return (
                <option key={domain.id || index} value={domain.id || index + 1}>
                  {domain.title || domain.name || `Domain ${index + 1}`}
                </option>
              );
            })
          }
        </select>
        {questionFlowErrors.domain_id && (
          <div className="invalid-feedback">{questionFlowErrors.domain_id}</div>
        )}
        {domains.length > 0 && (
          <small className="text-muted">
            Available domains: {domains.length} | Selected: {questionFlowFormData.domain_id}<sup>th</sup>
          </small>
        )}
      </div>
      
      <div className="mb-3">
        <label htmlFor="flowTitle" className="form-label">Title:</label>
        <input
          type="text"
          className={`form-control bg-white ${questionFlowErrors.title ? 'is-invalid' : ''}`}
          id="flowTitle"
          name="title"
          value={questionFlowFormData.title}
          onChange={handleQuestionFlowChange}
          disabled={loading}
        />
        {questionFlowErrors.title && (
          <div className="invalid-feedback">{questionFlowErrors.title}</div>
        )}
      </div>
      
      <div className="mb-3">
        <label htmlFor="flowDescription" className="form-label">Shortcode:</label>
        <input
          type="text"
          className={`form-control bg-white ${questionFlowErrors.description ? 'is-invalid' : ''}`}
          id="flowDescription"
          name="description"
          value={questionFlowFormData.description}
          onChange={handleQuestionFlowChange}
          disabled={loading}
        />
        {questionFlowErrors.description && (
          <div className="invalid-feedback d-block text-danger" style={{fontSize: '1rem'}}>{questionFlowErrors.description}</div>
        )}
      </div>
      
      <div className="mb-3">
        <label htmlFor="flowOverallScore" className="form-label">Overall Score:</label>
        <input
          type="number"
          className="form-control bg-white"
          id="flowOverallScore"
          name="overall_score"
          min="0"
          step="1"
          value={questionFlowFormData.overall_score}
          onChange={handleQuestionFlowChange}
          disabled={loading}
        />
      </div>
      
      <div className="mb-3">
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id="flowEnabled"
            name="is_enabled"
            checked={questionFlowFormData.is_enabled}
            onChange={handleQuestionFlowChange}
            disabled={loading}
          />
          <label className="form-check-label" htmlFor="flowEnabled">
            Enable Question Flow
          </label>
        </div>
      </div>
      
      <div className="mb-3">
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id="flowDeleted"
            name="is_deleted"
            checked={questionFlowFormData.is_deleted}
            onChange={handleQuestionFlowChange}
            disabled={loading}
          />
          <label className="form-check-label" htmlFor="flowDeleted">
            Mark as Deleted
          </label>
        </div>
      </div>
      
      <button
        type="button"
        className="btn btn-rounded"
        onClick={handleAddQuestionFlow}
        disabled={loading || domains.length === 0}
        style={{
          background: '#26397E',
          borderRadius: "77.125rem",
          transition: 'all 0.3s ease',
        }}
        onMouseEnter={(e) => {
          const target = e.target as HTMLButtonElement;
          target.style.backgroundColor = '#5376F0 !important';
          target.style.transform = 'translateY(-2px)';
        }}
        onMouseLeave={(e) => {
          const target = e.target as HTMLButtonElement;
          target.style.backgroundColor = '#26397E';
          target.style.transform = 'translateY(0)';
        }}
      >
        {loading ? (
          <div className="d-flex align-items-center">
            <div className="spinner-border spinner-border-sm me-2" role="status"></div>
            Adding...
          </div>
        ) : (
          'Add Question Flow'
        )}
      </button>
      
      {domains.length === 0 && (
        <div className="alert alert-warning mt-3">
          Please add at least one domain before creating question flows.
        </div>
      )}
        </>
      ) : (
        // List View
        <div className="mt-3">
          {questionFlows.length > 0 ? (
            <div>
              <div className="mb-2">
                <small className="text-muted">
                  <i className="bi bi-info-circle me-1"></i>
                  Question flows are organized by their parent domains. Each domain shows its question flows and their question counts.
                </small>
              </div>
              <DraggableQuestionFlowsTable />
          </div>
          ) : (
            <div className="text-center text-muted py-4">
              <p>No question flows added yet.</p>
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={() => setQuestionFlowViewMode('form')}
              >
                Add Your First Question Flow
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
  };

  // Render question form
  const renderQuestionForm = () => (
    <div className="mb-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0" style={{ color: '#183FCE', fontWeight: 'bold' }}>
        Add New Question
      </h6>
        <button
          type="button"
          className="btn btn-link p-0"
          onClick={() => setQuestionViewMode(questionViewMode === 'form' ? 'list' : 'form')}
          style={{ 
            color: '#183FCE', 
            textDecoration: 'none',
            fontSize: '12px',
            fontWeight: '500'
          }}
        >
          {questionViewMode === 'form' ? 'View Added Questions' : 'Back to Question Form'}
        </button>
      </div>

      {questionViewMode === 'form' ? (
        <>
      
      <div className="mb-3">
                    <label htmlFor="questionFlow" className="form-label">Select Question Flow:</label>
            <select
              className={`form-select bg-white ${questionErrors.flow_id ? 'is-invalid' : ''}`}
              id="questionFlow"
              name="flow_id"
              value={questionFormData.flow_id}
              onChange={handleQuestionChange}
              disabled={loading}
            >
              <option value={0}>Select a question flow</option>
              {questionFlows
                .filter(flow => flow.is_deleted !== true)
                .map((flow, index) => (
                  <option key={flow.id || index} value={flow.id || index + 1}>
                    {flow.title || flow.name || `Flow ${index + 1}`}
                  </option>
                ))
              }
            </select>
        {questionErrors.flow_id && (
          <div className="invalid-feedback">{questionErrors.flow_id}</div>
        )}
      </div>
      
      <div className="mb-3">
        <label htmlFor="questionText" className="form-label">Question Text:</label>
        <textarea
          className={`form-control bg-white ${questionErrors.question_text ? 'is-invalid' : ''}`}
          id="questionText"
          name="question_text"
          rows={3}
          value={questionFormData.question_text}
          onChange={handleQuestionChange}
          disabled={loading}
        />
        {questionErrors.question_text && (
          <div className="invalid-feedback">{questionErrors.question_text}</div>
        )}
      </div>
      
      <div className="mb-3">
        <label htmlFor="questionOrder" className="form-label">Order:</label>
        <input
          type="number"
          className={`form-control bg-white ${questionErrors.order ? 'is-invalid' : ''}`}
          id="questionOrder"
          name="order"
          min="1"
          value={questionFormData.order}
          onChange={handleQuestionChange}
          disabled={loading}
        />
        {questionErrors.order && (
          <div className="invalid-feedback">{questionErrors.order}</div>
        )}
      </div>
      
      <div className="mb-3">
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id="questionEnabled"
            name="is_enabled"
            checked={questionFormData.is_enabled}
            onChange={handleQuestionChange}
            disabled={loading}
          />
          <label className="form-check-label" htmlFor="questionEnabled">
            Enable Question
          </label>
        </div>
      </div>
      
      <div className="mb-3">
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id="questionDeleted"
            name="is_deleted"
            checked={questionFormData.is_deleted}
            onChange={handleQuestionChange}
            disabled={loading}
          />
          <label className="form-check-label" htmlFor="questionDeleted">
            Mark as Deleted
          </label>
        </div>
      </div>
      
      <button
        type="button"
        className="btn btn-rounded"
        onClick={handleAddQuestion}
        disabled={loading || questionFlows.length === 0}
        style={{
          background: '#26397E',
          borderRadius: "77.125rem",
          transition: 'all 0.3s ease',
        }}
        onMouseEnter={(e) => {
          const target = e.target as HTMLButtonElement;
          target.style.backgroundColor = '#5376F0 !important';
          target.style.transform = 'translateY(-2px)';
        }}
        onMouseLeave={(e) => {
          const target = e.target as HTMLButtonElement;
          target.style.backgroundColor = '#26397E';
          target.style.transform = 'translateY(0)';
        }}
      >
        {loading ? (
          <div className="d-flex align-items-center">
            <div className="spinner-border spinner-border-sm me-2" role="status"></div>
            Adding...
          </div>
        ) : (
          'Add Question'
        )}
      </button>
      
      {questionFlows.length === 0 && (
        <div className="alert alert-warning mt-3">
          Please add at least one question flow before creating questions.
        </div>
      )}
        </>
      ) : (
        // List View
        <div className="mt-3">
          {questions.length > 0 ? (
            <div>
              <div className="mb-2">
                <small className="text-muted">
                  <i className="bi bi-info-circle me-1"></i>
                  Questions are organized by their question flows. Drag questions by the grip handle (⋮⋮) to reorder them within each flow. Changes are saved automatically.
                </small>
              </div>
              <DraggableQuestionsTable />
            </div>
          ) : (
            <div className="text-center text-muted py-4">
              <p>No questions added yet.</p>
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={() => setQuestionViewMode('form')}
              >
                Add Your First Question
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  // Render scoring option form
  const renderScoringOptionForm = () => (
    <div className="mb-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0" style={{ color: '#183FCE', fontWeight: 'bold' }}>
          Add New Scoring Option
        </h6>
        <button
          type="button"
          className="btn btn-link p-0"
          onClick={() => setScoringOptionViewMode(scoringOptionViewMode === 'form' ? 'list' : 'form')}
          style={{ 
            color: '#183FCE', 
            textDecoration: 'none',
            fontSize: '12px',
            fontWeight: '500'
          }}
        >
          {scoringOptionViewMode === 'form' ? 'View Added Scoring Options' : 'Back to Scoring Option Form'}
        </button>
      </div>

      {scoringOptionViewMode === 'form' ? (
        <>
          <div className="mb-3">
            <label className="form-label">Framework:</label>
            <div className={`form-control bg-light ${scoringOptionErrors.framework_id ? 'is-invalid' : ''}`} style={{ backgroundColor: '#f8f9fa' }}>
              {frameworkData?.title || `Loading framework... (ID: ${scoringOptionFormData.framework_id})`}
            </div>
            {scoringOptionErrors.framework_id && (
              <div className="invalid-feedback">{scoringOptionErrors.framework_id}</div>
            )}
            {frameworkData && (
              <small className="text-muted">Framework ID: {frameworkData.id}</small>
            )}
          </div>
      
          <div className="mb-3">
            <label htmlFor="optionName" className="form-label">Option Name:</label>
            <input
              type="text"
              className={`form-control bg-white ${scoringOptionErrors.option_name ? 'is-invalid' : ''}`}
              id="optionName"
              name="option_name"
              value={scoringOptionFormData.option_name}
              onChange={handleScoringOptionChange}
              disabled={loading}
              placeholder="None, Partial, Risk Informed, etc"
            />
            {scoringOptionErrors.option_name && (
              <div className="invalid-feedback">{scoringOptionErrors.option_name}</div>
            )}
          </div>
          
          <div className="mb-3">
            <label htmlFor="optionScore" className="form-label">Score:</label>
            <input
              type="number"
              className={`form-control bg-white ${scoringOptionErrors.score ? 'is-invalid' : ''}`}
              id="optionScore"
              name="score"
              min="0"
              step="1"
              value={scoringOptionFormData.score}
              onChange={handleScoringOptionChange}
              disabled={loading}
            />
            {scoringOptionErrors.score && (
              <div className="invalid-feedback">{scoringOptionErrors.score}</div>
            )}
          </div>

          {/* Tooltip Content Section */}
          <div className="mb-3">
            <label className="form-label">Tooltip Content:</label>
            <div className="rounded" style={{ backgroundColor: '' }}>
              {tooltipKeys.map((key, keyIndex) => (
                <div key={keyIndex} className="mb-4 p-3 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
                  <div className="row g-2 mb-2">
                    <div className="col-md-4 ">
                      <label className="form-label small">Tooltip Key</label>
                      <input
                        type="text"
                        className="form-control bg-white"
                        placeholder="e.g., 'description', 'examples'"
                        value={key}
                        onChange={(e) => handleTooltipChange(keyIndex, 'key', e.target.value)}
                        disabled={loading}
                        style={{marginTop: '2.5%'}}
                      />
                    </div>
                    <div className="col-md-8">
                      <div className="d-flex justify-content-between align-items-end mb-2">
                        <label className="form-label small">Values</label>
                        {tooltipKeys.length > 1 && (
                          <i 
                            className="bi bi-trash"
                            style={{fontSize: '1rem', color: 'red', cursor: 'pointer'}}
                            onClick={() => {if (!loading) {removeTooltipKey(keyIndex)}}}
                            title="Remove this tooltip key"
                          ></i>
                      )}
                      </div>
                      <div style={{width: '100%'}}>
                        {key && scoringOptionFormData.tooltip_content[key] ? (
                          scoringOptionFormData.tooltip_content[key].map((value, valueIndex) => (
                            <div key={valueIndex} className="d-flex align-items-center mb-2">
                              <input
                                type="text"
                                className="form-control me-2 bg-white"
                                placeholder="Enter tooltip value"
                                value={value}
                                onChange={(e) => handleTooltipChange(keyIndex, 'value', e.target.value, valueIndex)}
                                disabled={loading}
                              />
                              <button
                                type="button"
                                className="btn btn-xs me-1"
                                onClick={() => addTooltipValue(keyIndex)}
                                disabled={loading}
                                title="Add another value"
                              >
                                <i className="bi bi-plus" style={{fontSize: '1rem', color: '#183FCE'}}></i>
                              </button>
                              {scoringOptionFormData.tooltip_content[key].length > 1 && (
                                <button
                                  type="button"
                                  className="btn btn-xs"
                                  onClick={() => removeTooltipValue(keyIndex, valueIndex)}
                                  disabled={loading}
                                  title="Remove this value"
                                >
                                  <i className="bi bi-dash" style={{fontSize: '1rem', color: 'red'}}></i>
                                </button>
                              )}
                            </div>
                          ))
                        ) : (
                          key && (
                            <div className="d-flex align-items-center mb-2">
                              <input
                                type="text"
                                className="form-control me-2"
                                placeholder="Enter tooltip value"
                                value=""
                                onChange={(e) => handleTooltipChange(keyIndex, 'value', e.target.value, 0)}
                                disabled={loading}
                              />
                              <button
                                type="button"
                                className="btn btn-outline-success btn-sm"
                                onClick={() => addTooltipValue(keyIndex)}
                                disabled={loading}
                                title="Add another value"
                              >
                                <i className="bi bi-plus"></i>
                              </button>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  </div>
              </div>
              ))}
              <div className='d-flex align-items-center justify-content-start'>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={addTooltipKey}
                  disabled={loading}
                  style={{
                    // background: '#26397E',
                    borderRadius: "77.125rem",
                    transition: 'all 0.3s ease',
                  }}
                  onMouseEnter={(e) => {
                    const target = e.target as HTMLButtonElement;
                    // target.style.backgroundColor = '#5376F0 !important';
                    target.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    const target = e.target as HTMLButtonElement;
                    // target.style.backgroundColor = '#26397E';
                    target.style.transform = 'translateY(0)';
                  }}
                >
                  <i className="bi bi-plus-circle me-1"></i>
                  Add Tooltip Key
                </button>
              </div>
          </div>
            <small className="text-muted">
              Add tooltip keys and their corresponding values. Click the <i className="bi bi-plus"></i> icon to add multiple values for each key.
            </small>
        </div>
          
          <div className="mb-3">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="optionEnabled"
                name="is_enabled"
                checked={scoringOptionFormData.is_enabled}
                onChange={handleScoringOptionChange}
                disabled={loading}
              />
              <label className="form-check-label" htmlFor="optionEnabled">
                Enable Scoring Option
              </label>
            </div>
          </div>
          
          <div className="mb-3">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="optionDeleted"
                name="is_deleted"
                checked={scoringOptionFormData.is_deleted}
                onChange={handleScoringOptionChange}
                disabled={loading}
              />
              <label className="form-check-label" htmlFor="optionDeleted">
                Mark as Deleted
              </label>
            </div>
          </div>
          
          <button
            type="button"
            className="btn btn-rounded"
            onClick={handleAddScoringOption}
            disabled={loading}
            style={{
              background: '#26397E',
              borderRadius: "77.125rem",
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={(e) => {
              const target = e.target as HTMLButtonElement;
              target.style.backgroundColor = '#5376F0 !important';
              target.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              const target = e.target as HTMLButtonElement;
              target.style.backgroundColor = '#26397E';
              target.style.transform = 'translateY(0)';
            }}
          >
            {loading ? (
              <div className="d-flex align-items-center">
                <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                Adding...
              </div>
            ) : (
              'Add Scoring Option'
            )}
          </button>
        </>
      ) : (
        // List View
        <div className="mt-3">
          <div style={{ 
            marginBottom: '24px', 
            border: '1px solid #dee2e6', 
            borderRadius: '8px', 
            backgroundColor: 'white',
            overflow: 'hidden'
          }}>
            {/* Header with background images */}
            <div style={{
              position: 'relative',
              color: 'white',
              padding: '12px 16px',
              fontSize: '14px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              overflow: 'hidden'
            }}>
              {/* Background Image Layer */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `url(${bgPublic})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                zIndex: 1
              }}></div>
              
              {/* Gray Overlay Layer */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `url(${grayBgTransparent})`,
                backgroundSize: 'cover',
                backgroundPosition: 'right',
                backgroundRepeat: 'no-repeat',
                zIndex: 2
              }}></div>
              
              {/* Content Layer */}
              <span style={{ position: 'relative', zIndex: 3 }}>
                {frameworkData?.title || 'Framework'} Scoring Options
              </span>
              <span className="badge bg-light text-dark" style={{ position: 'relative', zIndex: 3 }}>
                {scoringOptions.length} option{scoringOptions.length !== 1 ? 's' : ''}
              </span>
            </div>

            {/* Table Content */}
            {scoringOptions.length > 0 ? (
              <div>
                <AdminTables
                  data={scoringOptions}
                  columns={scoringOptionColumns}
                  rowSelectionEnabled={false}
                  onRowSelect={() => {}}
                  showSearch={false}
                  isLoading={false}
                  containerStyle={{
                    width: "100%",
                    overflowX: "auto"
                  }}
                />
              </div>
            ) : (
              <div className="text-center text-muted py-4">
                <p>No scoring options added yet.</p>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => setScoringOptionViewMode('form')}
                >
                  Add Your First Scoring Option
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  if (!isOpen) return null;

  return (
    <>
      <div
        className="offcanvas-backdrop fade show"
        onClick={handleClose}
      ></div>
      <div
        className="offcanvas offcanvas-end show"
        tabIndex={-1}
        style={{ 
          zIndex: 1050, 
          width: '90vw',
          maxWidth: '800px'
        }}
      >
        <div className="offcanvas-header" style={{backgroundColor: 'white', borderBottom: '1px solid #dee2e6'}}>
          <h5 className="offcanvas-title" style={{
            fontSize: '1.25rem',
            fontWeight: 'bold',
            color: '#183FCE'
          }}>
            Framework Setup: {frameworkData?.title}
          </h5>
          <button
            type="button"
            className="btn-close"
            onClick={handleClose}
          ></button>
        </div>
        
        {/* Stepper */}
        <div className="px-4 py-3" style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
          <div className="d-flex justify-content-between align-items-center">
            {steps.map((step, index) => (
              <div key={step.number} className="d-flex align-items-center">
                <div
                  className={`rounded-circle d-flex align-items-center justify-content-center ${
                    currentStep >= step.number ? 'bg-primary text-white' : 'bg-light text-muted'
                  }`}
                  style={{ width: '20%', height: '20%', fontSize: '14px', fontWeight: 'bold' }}
                >
                  {step.number}
                </div>
                <div className="ms-2">
                  <div className={`fw-bold ${currentStep >= step.number ? 'text-primary' : 'text-muted'}`} style={{ fontSize: '12px' }}>
                    {step.title}
                  </div>
                  <div className="text-muted" style={{ fontSize: '10px' }}>
                    {step.description}
                  </div>
                </div>
                {index < steps.length - 1 && (
                  <div 
                    className={`mx-3 ${currentStep > step.number ? 'bg-primary' : 'bg-light'}`} 
                    style={{ height: '2px', width: '60px' }}
                  ></div>
                )}
              </div>
            ))}
          </div>
        </div>
        
        <div className="offcanvas-body">
          {currentStep === 1 && renderDomainForm()}
          {currentStep === 2 && renderQuestionFlowForm()}
          {currentStep === 3 && renderQuestionForm()}
          {currentStep === 4 && renderScoringOptionForm()}
          
          {/* Navigation buttons */}
          <div className="d-flex justify-content-between mx-5">
            {currentStep > 1 ? (
              <div className="mb-3">
                <button
                  type="button"
                  className="btn btn-light btn-rounded"
                  onClick={handlePrevious}
                >
                  Previous
                </button>
              </div>
            ) : (
              <div className="mb-3"></div>
            )}

            {currentStep < 4 ? (
              <div className="mb-3">
                <button
                  type="button"
                  className="btn btn-light btn-rounded btn-rounded-next"
                  onClick={handleNext}
                >
                  Next
                </button>
              </div>
            ) : (
              <div className="mb-3">
                <button
                  type="button"
                  className="btn btn-light btn-rounded btn-rounded-next"
                  onClick={handleClose}
                >
                  Complete Setup
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Alert */}
      {alert && (
        <Alert
          message={alert.message}
          type={alert.type}
          show={alert.show}
          onClose={() => setAlert(null)}
        />
      )}
    </>
  );
};

export default FrameworkSetupSidescreen; 